import { useEffect, useMemo, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router'

import { TutorialStatus, useTutorialStore } from '@wwt/core/tutorial'
import { useCookiesStore } from '@wwt/widgets/popup/cookie-store'

import { cn } from '@/shared/lib/cn.ts'
import { useToast } from '@/shared/lib/hooks/useToast.tsx'
import { Button } from '@/shared/ui/Button.tsx'

import { wwtCookiesLog } from './wwtCookiesLog.ts'

export const useCookies = () => {
	const { t } = useTranslation('accept-cookies')

	const setCookiesAccepted = useCookiesStore.use.setCookiesAccepted()
	const cookiesAccepted = useCookiesStore.use.cookiesAccepted()
	const tutorialStatus = useTutorialStore.use.tutorialStatus()

	const { toast } = useToast()

	const isTutorialActive = useMemo(() => {
		return tutorialStatus === TutorialStatus.GOING
	}, [tutorialStatus])

	const cookiesToast = useRef<ReturnType<typeof toast> | null>(null)

	/**
	 * Here, we don’t need to worry about removing an item from the toasts list,
	 * as the toaster will handle it automatically via the onOpenChange prop
	 */
	const handleCookiesClose = () => {
		wwtCookiesLog('Set cookies accepted to true')
		setCookiesAccepted(true)
	}

	useEffect(() => {
		if (cookiesAccepted) {
			wwtCookiesLog("Don't show cookies because it already accepted")
			return
		}

		wwtCookiesLog('Show cookies popup')

		cookiesToast.current = toast({
			layer: 'modals',
			priority: true,
			duration: Infinity,
			className: cn('order-1', isTutorialActive && 'hidden'),
			onOpenChange: open => !open && handleCookiesClose(),
			description: (
				<>
					{t('text') + ' '}
					<Button
						className="inline size-fit p-0"
						variant="link"
						asChild
					>
						<Link
							to="/"
							data-wwt-id="cookies-popup__policy--link"
						>
							{t('policy')}
						</Link>
					</Button>
				</>
			)
		})

		return () => {
			if (cookiesToast.current === null) return

			wwtCookiesLog('Remove cookies popup')

			cookiesToast.current.dismiss()
		}
	}, [cookiesAccepted, tutorialStatus])
}
